import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip, Typography,
  IconButton, CircularProgress, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useIsMobile from '../hooks/useIsMobile';
import { AlerteRegle, Journal, journalAlerteRegle } from '../services/alertesReglesServices';

interface Props {
  open: boolean;
  regle: AlerteRegle | null;
  onClose: () => void;
}

// Journal d'une règle : marqueurs alertes_planifiees regroupés par passe (date_envoi) et statut
const AlerteJournalDialog: React.FC<Props> = ({ open, regle, onClose }) => {
  const isMobile = useIsMobile();
  const [journal, setJournal] = useState<Journal | null>(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !regle) return;
    let annule = false;
    setLoading(true);
    setErreur(null);
    setJournal(null);
    journalAlerteRegle(regle.id)
      .then((j) => { if (!annule) setJournal(j); })
      .catch((e) => {
        console.error('Erreur chargement journal', e);
        if (!annule) setErreur('Chargement du journal impossible');
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [open, regle]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6 }}>
        Journal — {regle ? regle.libelle : ''}
        <Typography variant="caption" display="block" color="text.secondary">
          {journal ? `${journal.nb_total} marqueur(s)` : ' '}
          {journal && journal.nb_total > 500 ? ' — les 500 plus récents sont affichés' : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        )}
        {erreur && <Alert severity="error">{erreur}</Alert>}
        {!loading && journal && journal.passes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Aucun envoi pour cette règle
          </Typography>
        )}
        {!loading && journal && journal.passes.map((p, i) => (
          <Box key={`${p.passe || 'echec'}-${p.statut}-${i}`} sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                bgcolor: '#f5f5f5', px: 1.5, py: 0.75, borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {p.passe || 'Sans date d’envoi'} — {p.dossiers.length} dossier(s)
              </Typography>
              {p.statut === 'envoye' ? (
                <Chip size="small" color="success" label="Envoyé" />
              ) : (
                <Chip size="small" color="error" label="En échec, nouvelle tentative à la prochaine passe" />
              )}
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 3, py: 0.5 }}>
              {p.dossiers.map((d) => (
                <Typography component="li" variant="body2" key={d.id_demande} sx={{ py: 0.25 }}>
                  #{d.id_demande} — {d.chemin || 'sans réf.'} — {d.libelle_visa || '(dossier introuvable)'} — départ{' '}
                  {d.date_depart || '—'}
                  {d.message ? (
                    <Typography component="span" variant="caption" color="text.secondary"> — {d.message}</Typography>
                  ) : null}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlerteJournalDialog;
